"use client";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import moment from "moment";
import React, { useState } from "react";
import CCheckRadio from "../CCheckRadio/CCheckRadio";
import CPagination from "../CPagination/CPagination";
import { flattenValues } from "./utils/flattenValues";
import { getNestedValue } from "./utils/getNestedValue";
import { getSortableValue } from "./utils/getSortableValue";

export interface TableColumn<T> {
  key: keyof T;
  label: string;
  headerRender?: () => React.ReactNode;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
  sortable?: boolean;
  align?: "left" | "center" | "right";
  headerClassName?: string;
  cellClassName?: string;
}

interface TableProps<T> {
  title?: React.ReactNode;
  secondary?: string | React.ReactNode;
  columns: TableColumn<T>[];
  data: T[];
  selectableRows?: boolean;
  onSelectionChange?: (selected: T[]) => void;
  headerClassName?: React.HTMLAttributes<HTMLTableSectionElement>["className"];
  align?: "left" | "center" | "right";
  tableSearchable?: boolean;
  frontendPagination?: boolean;
  defaultPageSize?: number;
  loading?: boolean;
  rowClickable?: boolean;
  rowClick?: (row: T, rowIndex?: number) => void;
  footer?: React.ReactNode;
}

export default function CTable<T>({
  title,
  secondary,
  columns,
  data,
  selectableRows = false,
  onSelectionChange,
  headerClassName,
  align = "left",
  tableSearchable = false,
  frontendPagination = false,
  defaultPageSize = 20,
  loading = false,
  rowClickable = false,
  rowClick,
  footer,
}: TableProps<T>) {
  const [copiedCell, setCopiedCell] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof T | null;
    direction: "asc" | "desc";
  }>({ key: null, direction: "asc" });
  const [search, setSearch] = useState("");

  // Pagination state for frontend pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const filteredData = React.useMemo(() => {
    if (!tableSearchable || !search.trim()) return data;
    const lower = search.trim().toLowerCase();
    return data.filter((row) =>
      columns.some((col) => {
        const key = String(col.key);
        const value = getNestedValue(row, key);
        if (value === null || value === undefined) return false;
        // Search inside nested objects too
        const valuesToSearch = flattenValues(value);
        return valuesToSearch.some((v) => v.toLowerCase().includes(lower));
      }),
    );
  }, [tableSearchable, search, data, columns]);
  // Handle sorting functionality
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return filteredData;
    const key = String(sortConfig.key);

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = getSortableValue(a, key);
      const bValue = getSortableValue(b, key);

      if (aValue === bValue) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }
      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
    return sorted;
  }, [filteredData, sortConfig.direction, sortConfig.key]);

  // Apply frontend pagination if enabled
  const paginatedData = React.useMemo(() => {
    if (!frontendPagination) return sortedData;
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, frontendPagination, currentPage, pageSize]);

  // Reset to first page if data changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [sortedData]);

  // Handle sorting button click
  const handleSort = (key: keyof T) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  // Handle select all rows
  const toggleAll = () => {
    const allSelected = selectedRows.length === sortedData.length;
    const newSelected = allSelected ? [] : sortedData.map((_, idx) => idx);
    setSelectedRows(newSelected);
    onSelectionChange?.(allSelected ? [] : sortedData);
  };

  // Handle row selection
  const toggleRow = (index: number) => {
    const newSelected = selectedRows.includes(index)
      ? selectedRows.filter((i) => i !== index)
      : [...selectedRows, index];
    setSelectedRows(newSelected);
    onSelectionChange?.(newSelected.map((i) => sortedData[i]));
  };

  // Get alignment classes for header
  const getHeaderAlignClass = (col: TableColumn<T>) => {
    // Check headerClassName first for explicit alignment
    if (col.headerClassName?.includes("text-right")) return "justify-end";
    if (col.headerClassName?.includes("text-left")) return "justify-start";
    // Then check align prop
    if (col.align === "right") return "justify-end";
    if (col.align === "left") return "justify-start";
    return "justify-center";
  };

  // Get alignment for cell
  const getCellAlign = (col: TableColumn<T>): "left" | "center" | "right" => {
    return col.align || "center";
  };

  const hasHeader = Boolean(title || secondary || tableSearchable);

  return (
    <div>
      {hasHeader && (
        <div className="flex flex-col gap-1 xs:flex-row xs:justify-between xs:items-center mb-4">
          {title && (
            <div>
              {typeof title === "string" ? (
                <h2 className="text-left md:text-base text-xs font-semibold text-foreground">
                  {title}
                </h2>
              ) : (
                title
              )}
            </div>
          )}
          <div className="flex items-center gap-2 lg:flex-row flex-col justify-between">
            {secondary && typeof secondary === "string" ? (
              <span className="text-sm text-muted-foreground">{secondary}</span>
            ) : (
              secondary
            )}
            {tableSearchable && (
              <Input
                type="search"
                autoComplete="off"
                placeholder={
                  "Search " +
                  columns
                    .filter((col) => {
                      const label = col.label?.toLowerCase();
                      return label !== "action" && label !== "actions";
                    })
                    .map((col) => col.label)
                    .join(", ")
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            )}
          </div>
        </div>
      )}
      <div
        className="w-full overflow-x-auto border border-border/50 rounded-lg hide-scrollbar"
        style={{
          marginBottom: frontendPagination ? "1rem" : "0",
        }}
      >
        <table className="min-w-full bg-card text-card-foreground text-sm">
          <thead
            className={cn(
              "bg-primary/10 text-secondary-foreground text-xs font-semibold",
              headerClassName,
            )}
          >
            <tr>
              {selectableRows && (
                <th className="px-4 py-3 text-left">
                  <CCheckRadio
                    type="checkbox"
                    checked={
                      sortedData.length > 0 &&
                      selectedRows.length === sortedData.length
                    }
                    onChange={toggleAll}
                  />
                </th>
              )}
              {columns.map((col) => {
                const colAlign = getCellAlign(col);
                return (
                  <th
                    key={String(col.key)}
                    className={cn(
                      "px-4 py-3 whitespace-nowrap uppercase text-slate-600 dark:text-slate-400 tracking-wider",
                      `text-${colAlign}`,
                      col.headerClassName,
                    )}
                  >
                    <span
                      className={cn(
                        "flex items-center gap-1",
                        getHeaderAlignClass(col),
                      )}
                    >
                      {col.headerRender ? col.headerRender() : col.label}
                      {col.sortable && (
                        <button
                          type="button"
                          className="ml-1 text-muted-foreground hover:text-foreground focus:outline-none"
                          onClick={() => handleSort(col.key)}
                          tabIndex={0}
                          aria-label={`Sort by ${col.label}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: 0,
                            background: "none",
                            border: "none",
                          }}
                        >
                          {sortConfig.key !== col.key ? (
                            <ArrowUpDown size={16} />
                          ) : sortConfig.direction === "asc" ? (
                            <ArrowUp size={16} />
                          ) : (
                            <ArrowDown size={16} />
                          )}
                        </button>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {loading ? (
              <tr>
                <td
                  colSpan={selectableRows ? columns.length + 1 : columns.length}
                  className="text-center py-8 text-muted-foreground text-base"
                >
                  Loading data...
                </td>
              </tr>
            ) : paginatedData?.length === 0 ? (
              <tr>
                <td
                  colSpan={selectableRows ? columns.length + 1 : columns.length}
                  className="text-center py-8 text-muted-foreground text-base"
                >
                  No data available
                </td>
              </tr>
            ) : (
              paginatedData?.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                  onClick={() => {
                    if (rowClickable && rowClick) {
                      rowClick(row, rowIndex);
                    }
                  }}
                >
                  {selectableRows && (
                    <td className="px-4 py-4">
                      <CCheckRadio
                        type="checkbox"
                        checked={selectedRows.includes(rowIndex)}
                        onChange={() => toggleRow(rowIndex)}
                      />
                    </td>
                  )}
                  {columns.map((col) => {
                    const key = String(col.key);
                    const value = getNestedValue(row, key);
                    const colAlign = getCellAlign(col);
                    // Compose a unique cell key for feedback
                    const cellId = `${rowIndex}-${key}`;
                    // Determine display value
                    const displayValue = col.render
                      ? col.render(value as T[keyof T], row)
                      : typeof value === "string" &&
                          value &&
                          moment(String(value), moment.ISO_8601, true).isValid()
                        ? moment(String(value)).format("YYYY-MM-DD h:mm A")
                        : String(value);
                    return (
                      <td
                        key={key}
                        className={cn(
                          "px-4 py-4 whitespace-nowrap text-sm text-muted-foreground",
                          `text-${colAlign}`,
                          copiedCell === cellId &&
                            "bg-green-100 dark:bg-green-900",
                          col.cellClassName,
                        )}
                      >
                        {displayValue ? (
                          <span
                            className="block"
                            title={
                              typeof displayValue === "string"
                                ? displayValue
                                : value != null
                                  ? String(value)
                                  : undefined
                            }
                          >
                            {displayValue}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
        {/* Custom footer (e.g., pagination) */}
        {footer && (
          <div className="px-4 py-3 border-t border-border/50">{footer}</div>
        )}
      </div>
      {/* Frontend pagination controls */}
      {frontendPagination && (
        <CPagination
          currentPage={currentPage}
          totalCount={sortedData.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </div>
  );
}
